import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Panel } from "@/lovable-demo/components/bits";
import { supabase } from "@/lovable-demo/runtime/client";
import type { ProblemRecord } from "@/lovable-demo/lib/warranty/types";

/**
 * Consolidated, read-only Decision History for one Problem Record.
 * Reads the append-only audit trail and escalation events — no writes, no new source of truth.
 */

type Category =
  "Recommendation" | "PFMEA" | "Compliance" | "Escalation" | "Signal" | "Financial" | "Evidence";

const CATEGORY_BY_PREFIX: [string, Category][] = [
  ["recommendation.", "Recommendation"],
  ["pfmea.", "PFMEA"],
  ["compliance.", "Compliance"],
  ["escalation.", "Escalation"],
  ["signal.", "Signal"],
  ["record.created_from_signal", "Signal"],
  ["record.signal_linked", "Signal"],
  ["financial.", "Financial"],
  ["evidence.", "Evidence"],
];

function categorize(eventType: string): Category | null {
  for (const [p, c] of CATEGORY_BY_PREFIX) if (eventType.startsWith(p)) return c;
  return null;
}

interface Row {
  id: string;
  at: string;
  category: Category;
  action: string;
  actor: string;
  verdict: string | null;
  detail: string;
  rule: string | null;
}

export function DecisionHistory({ record }: { record: ProblemRecord }) {
  const [filter, setFilter] = useState<Category | "All">("All");
  const q = useQuery({
    queryKey: ["lovable-demo", "decision-history", record.id],
    queryFn: async (): Promise<Row[]> => {
      const [audit, esc] = await Promise.all([
        supabase
          .from("audit_events")
          .select(
            "id,occurred_at,event_type,action,actor_label,decision_verdict,detail,authority_rule_key",
          )
          .or(`entity_id.eq.${record.id},record_code.eq.${record.code}`)
          .order("occurred_at", { ascending: false }),
        supabase
          .from("escalation_events")
          .select(
            "id,occurred_at,event_type,from_status,to_status,actor_label,detail,audit_event_id",
          )
          .eq("record_id", record.id)
          .order("occurred_at", { ascending: false }),
      ]);
      if (audit.error) throw audit.error;
      if (esc.error) throw esc.error;
      const seen = new Set<string>();
      const rows: Row[] = [];
      for (const a of audit.data ?? []) {
        const c = categorize(a.event_type);
        if (!c) continue;
        seen.add(a.id);
        rows.push({
          id: a.id,
          at: a.occurred_at,
          category: c,
          action: a.action,
          actor: a.actor_label,
          verdict: a.decision_verdict,
          detail: a.detail,
          rule: a.authority_rule_key,
        });
      }
      for (const e of esc.data ?? []) {
        if (e.audit_event_id && seen.has(e.audit_event_id)) continue;
        const move =
          e.from_status && e.to_status && e.from_status !== e.to_status
            ? `${e.from_status} → ${e.to_status}`
            : null;
        rows.push({
          id: e.id,
          at: e.occurred_at,
          category: "Escalation",
          action: e.event_type.replace(/[._]/g, " "),
          actor: e.actor_label,
          verdict: move,
          detail: e.detail,
          rule: null,
        });
      }
      return rows.sort((x, y) => y.at.localeCompare(x.at));
    },
  });

  const rows = q.data ?? [];
  const cats = useMemo(() => Array.from(new Set(rows.map((r) => r.category))), [rows]);
  const shown = filter === "All" ? rows : rows.filter((r) => r.category === filter);

  return (
    <Panel
      title={`Decision history — ${rows.length}`}
      subtitle="Every governed decision on this record, who made it, when and why. Read from the append-only audit trail."
    >
      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading decision history…</p>
      ) : q.isError ? (
        <p className="text-sm text-destructive">
          Decision history could not be loaded. {(q.error as Error).message}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No governed decisions have been recorded on this record yet.
        </p>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap gap-1.5">
            {(["All", ...cats] as const).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setFilter(c)}
                className={`rounded-full border px-2.5 py-0.5 text-xs ${filter === c ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="py-2 pr-3 font-medium">When</th>
                  <th className="py-2 pr-3 font-medium">Area</th>
                  <th className="py-2 pr-3 font-medium">Decision / action</th>
                  <th className="py-2 pr-3 font-medium">Outcome</th>
                  <th className="py-2 pr-3 font-medium">By</th>
                  <th className="py-2 font-medium">Rationale / detail</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 align-top last:border-0">
                    <td className="whitespace-nowrap py-2 pr-3 text-xs text-muted-foreground">
                      {new Date(r.at).toLocaleString()}
                    </td>
                    <td className="py-2 pr-3">
                      <span className="rounded bg-muted px-1.5 py-0.5 text-xs">{r.category}</span>
                    </td>
                    <td className="py-2 pr-3">{r.action}</td>
                    <td className="py-2 pr-3 text-xs">{r.verdict ?? "—"}</td>
                    <td className="py-2 pr-3 text-xs">
                      {r.actor || "—"}
                      {r.rule && <div className="text-muted-foreground">Authority: {r.rule}</div>}
                    </td>
                    <td className="py-2 text-xs text-muted-foreground">{r.detail || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Panel>
  );
}
